import React, { useMemo, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import { MainSection } from '@/components/layout/MainSection';
import { DatePager } from '@/components/DatePager';
import { MatchesFilterBar } from '@/components/MatchesFilterBar';
import { LeagueMatchesGroup, MatchRowsCard } from '@/components/LeagueMatchesGroup';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { useLocalToday } from '@/hooks/useLocalToday';
import { useHistoryEntryState } from '@/hooks/useHistoryEntryState';
import { isIntegerId, useSessionIdSet } from '@/hooks/useSessionIdSet';
import {
  countGroupMatches,
  filterMatchesByDateGroups,
  flattenMatchesByTime,
  hasLiveMatch,
} from '@/utils/matchesByDateFilter';
import { needsMatchPolling } from '@/utils/matchPolling';
import type { MatchesByDateResponse } from '@/types/league';
import styles from '../LeaguesPage.module.scss';

/** Collapsed league ids, kept for the browser tab across day switches and reloads. */
const COLLAPSED_LEAGUES_STORAGE_KEY = 'matches-by-date:collapsed-leagues';
const POLL_INTERVAL_MS = 60_000;
/** History-entry key of the selected day (restored on history back). */
const DATE_HISTORY_KEY = 'matchesByDate:date';

const isIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

type EmptyReason = 'live' | 'filter' | 'day';

/** Poll while something is live or about to kick off; stay quiet otherwise. */
function needsPolling(payload: MatchesByDateResponse | undefined): boolean {
  return needsMatchPolling((payload?.data?.leagues ?? []).flatMap(group => group.matches));
}

/**
 * `/matches` — date-first fixture browser: every match of the selected calendar
 * day, grouped by league into collapsible cards (or one kickoff-sorted list in
 * "By time" mode), with Live and team/league filters.
 *
 * Live updates: the query polls every 60s while the payload holds a live match
 * or one kicking off within five minutes (mirroring the backend's own 60s
 * upstream poll) and stops otherwise; between polls `useLiveTick` inside the
 * card extrapolates the displayed minute.
 */
const MatchesByDateTab: React.FC = () => {
  const { t } = useTranslation(['league', 'common']);

  const today = useLocalToday();
  // The chosen day survives a history-back return (e.g. from a match page);
  // a fresh visit (bottom nav, dashboard) starts from today.
  const [date, setDate] = useHistoryEntryState(DATE_HISTORY_KEY, today, isIsoDate);
  // Midnight rollover: a user parked on "Today" follows the new day; any other
  // explicitly chosen day stays put. (State adjusted during render — no effect.)
  const [prevToday, setPrevToday] = useState(today);
  if (today !== prevToday) {
    setPrevToday(today);
    if (date === prevToday) setDate(today);
  }
  const [liveOnly, setLiveOnly] = useState(false);
  const [byTime, setByTime] = useState(false);
  const [query, setQuery] = useState('');
  const collapsed = useSessionIdSet(COLLAPSED_LEAGUES_STORAGE_KEY, isIntegerId);

  // The text filter is hidden below tablet; a query typed on a wider viewport
  // must not keep filtering invisibly after a resize / rotation.
  const isPhone = useBreakpointDown('tablet');
  const activeQuery = isPhone ? '' : query;

  const { data, isLoading, isPlaceholderData, error } = useQuery<MatchesByDateResponse>({
    queryKey: ['matches-by-date', date],
    queryFn: async () => (await apiClient.getMatchesByDate(date)).data,
    // Date-keyed query consumed by a `return null` loading gate — without
    // keepPreviousData every day switch would unmount the whole list.
    placeholderData: keepPreviousData,
    refetchInterval: (q) => (needsPolling(q.state.data) ? POLL_INTERVAL_MS : false),
    refetchIntervalInBackground: false,
  });

  const groups = useMemo(() => data?.data?.leagues ?? [], [data]);
  const totalMatches = data?.meta?.total_matches ?? 0;
  const dayHasLive = useMemo(() => hasLiveMatch(groups), [groups]);

  const visibleGroups = useMemo(
    () => filterMatchesByDateGroups(groups, { liveOnly, query: activeQuery }),
    [groups, liveOnly, activeQuery],
  );
  const timeline = useMemo(
    () => (byTime ? flattenMatchesByTime(visibleGroups) : []),
    [byTime, visibleGroups],
  );

  const isFiltered = liveOnly || activeQuery.trim() !== '';
  const subtitle = isFiltered
    ? t('league:matchCountFiltered', { count: countGroupMatches(visibleGroups), total: totalMatches })
    : t('league:matchCount', { count: totalMatches });

  const visibleIds = visibleGroups.map(group => group.league.id);
  const allCollapsed = visibleIds.length > 0 && visibleIds.every(id => collapsed.ids.has(id));

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingMatches')}</p>;
  } else if (isLoading) {
    body = null;
  } else if (visibleGroups.length === 0) {
    let reason: EmptyReason = 'day';
    if (groups.length > 0) reason = liveOnly && !activeQuery.trim() ? 'live' : 'filter';
    const emptyText = {
      day: t('league:noMatchesOnDay'),
      live: t('league:noLiveMatches'),
      filter: t('league:noFilterResults'),
    }[reason];
    body = (
      <p className={styles.empty} data-testid="matches-by-date-empty" data-empty-reason={reason}>
        {emptyText}
      </p>
    );
  } else if (byTime) {
    body = <MatchRowsCard matches={timeline} testId="matches-by-time" />;
  } else {
    body = (
      <div className={styles.groups}>
        {visibleGroups.map(({ league, matches }) => (
          <LeagueMatchesGroup
            key={league.id}
            league={league}
            matches={matches}
            expanded={!collapsed.ids.has(league.id)}
            onToggle={() => collapsed.toggle(league.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <MainSection
      hideTitleOnMobile
      title={t('league:matchesTitle')}
      subtitle={subtitle}
    >
      <div className={styles.wrapper}>
        <div className={styles.toolbar}>
          <DatePager value={date} today={today} onChange={setDate} />
          <MatchesFilterBar
            liveOnly={liveOnly}
            onLiveOnlyChange={setLiveOnly}
            hasLive={dayHasLive}
            byTime={byTime}
            onByTimeChange={setByTime}
            query={query}
            onQueryChange={setQuery}
            allCollapsed={allCollapsed}
            onToggleAll={() => collapsed.setMany(visibleIds, !allCollapsed)}
            collapseAllDisabled={byTime || visibleIds.length === 0}
          />
        </div>

        {/* `data-date` is the day actually rendered; while the next day loads the
            previous one stays on screen, dimmed and marked busy. */}
        <div
          className={styles.dayBody}
          data-testid="matches-by-date-body"
          data-date={data?.meta?.date}
          aria-busy={isPlaceholderData}
        >
          {body}
        </div>
      </div>
    </MainSection>
  );
};

export default MatchesByDateTab;
